import { useEffect, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { navItems, newsItems } from "./landingContent";
import { LandingButton } from "./LandingPrimitives";

function hashFromNavHref(href: string): string | null {
  const i = href.indexOf("#");
  if (i === -1) return null;
  const hash = href.slice(i);
  return hash.length > 1 ? hash : null;
}

export default function LandingNavbar() {
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth > 960) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const previousBodyBg = document.body.style.backgroundColor;
    document.documentElement.classList.add("landing-menu-open");
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.documentElement.style.backgroundColor = "#2a2544";
    document.body.style.backgroundColor = "#2a2544";
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      document.documentElement.classList.remove("landing-menu-open");
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.backgroundColor = previousHtmlBg;
      document.body.style.backgroundColor = previousBodyBg;
    };
  }, [menuOpen]);

  const visibleNavItems =
    newsItems.length > 0
      ? navItems
      : navItems.filter((item) => item.href !== "/#noutati");

  const goToTop = (e: MouseEvent<HTMLAnchorElement>) => {
    setMenuOpen(false);
    if (pathname !== "/") return;
    e.preventDefault();
    if (hash) {
      void navigate({ pathname: "/", hash: "" }, { replace: true });
    }
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const goToNavItem = (href: string, e: MouseEvent<HTMLAnchorElement>) => {
    const targetHash = hashFromNavHref(href);
    if (!targetHash) {
      setMenuOpen(false);
      return;
    }
    e.preventDefault();
    setMenuOpen(false);
    if (pathname === "/") {
      void navigate({ pathname: "/", hash: targetHash }, { replace: true });
    } else {
      void navigate({ pathname: "/", hash: targetHash });
    }
  };

  return (
    <>
      {menuOpen ? (
        <button
          type="button"
          className="landing-nav__backdrop"
          tabIndex={-1}
          aria-label="Închide meniul"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <header className={`landing-nav${menuOpen ? " landing-nav--open" : ""}`}>
        <div className="landing-shell landing-nav__inner">
          <Link to="/" className="landing-brand" onClick={goToTop}>
            <img
              src="/favicon.png"
              alt="Logo AIFoiParcurs"
              aria-hidden="true"
              className="landing-brand__icon"
            />
            <span>AIFoiParcurs</span>
          </Link>

          <nav className="landing-nav__links" aria-label="Secțiuni">
            {visibleNavItems.map((item) =>
              hashFromNavHref(item.href) ? (
                <a key={item.label} href={item.href} onClick={(e) => goToNavItem(item.href, e)}>
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  aria-current={pathname === item.href ? "page" : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="landing-nav__actions">
            <LandingButton href="/login" variant="secondary" className="landing-nav__cta">
              Login
            </LandingButton>

            <button
              type="button"
              className={`landing-nav__toggle${menuOpen ? " is-open" : ""}`}
              aria-label={menuOpen ? "Închide meniul" : "Deschide meniul"}
              aria-expanded={menuOpen}
              aria-controls="landing-nav-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="landing-nav__toggle-bar" />
              <span className="landing-nav__toggle-bar" />
              <span className="landing-nav__toggle-bar" />
            </button>
          </div>
        </div>

        <nav id="landing-nav-menu" className="landing-nav__panel" aria-label="Secțiuni">
          <div className="landing-shell landing-nav__panel-inner">
            {visibleNavItems.map((item) =>
              hashFromNavHref(item.href) ? (
                <a key={item.label} href={item.href} onClick={(e) => goToNavItem(item.href, e)}>
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  aria-current={pathname === item.href ? "page" : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ),
            )}
          </div>
        </nav>
      </header>
    </>
  );
}
